/* eslint-disable max-lines */
// Single chat panel shared by every agent (ls-rag and the Skills Engine
// agents alike) — the dispatch/session/routing-state logic that makes that
// work lives together here rather than being split across near-duplicate
// per-agent components. Same call as ObsMetricSubTabs.tsx's.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { message, Upload, Row, Col } from 'antd';
import DCAIcon from '../../../assets/icons/GenAi/dca-icon.svg';
import './CopilotAgentsStyles.less';
import LifeSciencesRagService, {
  ILsRagDocument,
  ILsRagMessage,
  ILsRagSession,
} from '../../../libs/services/LifeSciencesRagService/LifeSciencesRagService';
import HallucinationModal, { ADVERSARIAL_QUERIES } from './HallucinationModal';
import ChatMessage from './ChatMessage';
import AgentsSidebar, { AGENTS } from './AgentsSidebar';
import CostEstimator from './CostEstimator';
import ModelBar from './ModelBar';
import { AVAILABLE_MODELS } from '../../../constants/AvailableModels';
import { RoutingMode } from '../../../libs/models/GenAIModels';
import {
  _skillsEngineChat, _skillsEngineListSessions, _skillsEngineCreateSession,
  ICopilotAgentsProps, HINTS, WELCOME, getWelcomeMsg,
} from './CopilotAgentsHelpers';
import { consumePendingAgent } from '../../../libs/utils/copilotSignal';

// ls-rag's own /ls-rag/sessions is hardcoded server-side to agent_id="ls-rag"
// (so it can't accidentally list another agent's sessions on that dedicated
// route); every other agent lists/creates via the generic /api/sessions
// route instead. Centralizing the dispatch here means every call site below
// (initial load, agent switch, "New Agent Session", post-send refresh)
// automatically shows the selected agent's own history instead of always
// ls-rag's.
const listSessionsForAgent = (agent: typeof AGENTS[number]) =>
  agent.isRag ? LifeSciencesRagService.listSessions() : _skillsEngineListSessions(agent.agentId ?? '');

const createSessionForAgent = (agent: typeof AGENTS[number]) =>
  agent.isRag ? LifeSciencesRagService.createSession() : _skillsEngineCreateSession(agent.agentId ?? '');

const CopilotAgents: React.FC<ICopilotAgentsProps> = ({ open, onClose, embedded = false }) => {
  const [documents,     setDocuments]     = useState<ILsRagDocument[]>([]);
  const [sessions,      setSessions]      = useState<ILsRagSession[]>([]);
  const [activeSession, setActiveSession] = useState<string | null>(null);
  const [messages,      setMessages]      = useState<ILsRagMessage[]>([WELCOME]);
  const [input,         setInput]         = useState('');
  const [querying,      setQuerying]      = useState(false);
  const [uploading,     setUploading]     = useState(false);
  const [deletingDoc,   setDeletingDoc]   = useState<string | null>(null);
  const [deletingSess,  setDeletingSess]  = useState<string | null>(null);
  const [selectedAgent,   setSelectedAgent]   = useState(0);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [showHallucinationModal, setShowHallucinationModal] = useState(false);
  const [selectedHQIdx, setSelectedHQIdx] = useState(0);
  const [routingMode, setRoutingMode] = useState<RoutingMode>('auto');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [disableCache, setDisableCache] = useState<boolean>(false);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Always holds the session currently being viewed, kept in sync by the
  // session-load effect below (including the null/"no session yet" case).
  // Async work (session history fetch, in-flight query) reads this at
  // resolve-time to detect "the user navigated elsewhere while I was
  // waiting" and avoid clobbering the wrong session's messages.
  const activeSessionRef = useRef<string | null>(null);
  // Set right before handleSend promotes activeSession from null to a
  // freshly-created session id. ls_rag_messages (the DB table backing
  // session history) has no model/tier/cache_hit/cost columns — those only
  // ever exist on the live response object. Without this flag, promoting
  // activeSession triggers the session-load effect below, which reloads
  // that session's (badge-less) DB copy on top of the just-rendered live
  // reply, silently wiping its model/cache/cost badges.
  const skipNextSessionLoadRef = useRef(false);

  const toggleSource = (key: string) => {
    setExpandedSources(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Scroll only the message pane itself (never scrollIntoView, which walks
  // up and can also scroll ancestor/page containers, producing a jump
  // outside the chat box).
  const scrollToBottom = () => {
    const el = chatBodyRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };
  // Only auto-scroll for messages appended live (handleSend) — never for the
  // initial welcome message or a session's loaded history, so switching into
  // this tab / picking a past session doesn't fight the tab-switch transition
  // with a forced scroll-to-bottom jump.
  const autoScrollRef = useRef(false);

  // Pre-select agent when opened via "Test Agent" signal from Agent Registry
  useEffect(() => {
    const agentId = consumePendingAgent();
    if (agentId) {
      const idx = AGENTS.findIndex((a) => a.agentId === agentId);
      if (idx >= 0) setSelectedAgent(idx);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e: Event) => {
      const agentId = (e as CustomEvent<{ agentId?: string }>).detail?.agentId;
      if (agentId) {
        const idx = AGENTS.findIndex((a) => a.agentId === agentId);
        if (idx >= 0) setSelectedAgent(idx);
      }
    };
    window.addEventListener('dca:open-copilot', handler);
    return () => window.removeEventListener('dca:open-copilot', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    LifeSciencesRagService.listDocuments().then(res => setDocuments(res.documents)).catch(() => {});
  }, [open]);

  // Reset chat + reload session history when the user switches agents (also
  // fires on initial open, since selectedAgent is already set by then).
  // Sessions must reload here too — each agent's chat history is its own
  // (agent_id-scoped server-side), so leaving the previous agent's session
  // list on screen after switching would let you click into a session that
  // belongs to a different agent entirely. Routing state resets for a
  // similar reason: each agent has its own allowedModels, and a
  // manually-selected model left over from a different agent could be
  // outside the new agent's allowed set (the backend trusts the UI to only
  // ever offer valid choices in manual mode; it doesn't clamp a stale one).
  useEffect(() => {
    if (!open) return;
    setMessages([getWelcomeMsg(selectedAgent)]);
    setActiveSession(null);
    setRoutingMode('auto');
    setSelectedModel('');
    setDisableCache(false);
    setSessions([]);
    listSessionsForAgent(AGENTS[selectedAgent]).then(res => setSessions(res.sessions)).catch(() => {});
  }, [open, selectedAgent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoScrollRef.current) scrollToBottom();
  }, [messages, querying]);

  useEffect(() => {
    activeSessionRef.current = activeSession;
    autoScrollRef.current = false;
    if (!activeSession) { setMessages([getWelcomeMsg(selectedAgent)]); return; }
    if (skipNextSessionLoadRef.current) {
      skipNextSessionLoadRef.current = false;
      return;
    }
    const requestedSession = activeSession;
    // Clear immediately (rather than leaving the previous session's messages
    // on screen) so the "hasRealMessages" guard below can only mean "the
    // user sent something new while this load was in flight" — not "the
    // previously-viewed session happened to have real messages too", which
    // used to make switching between two existing sessions silently no-op.
    setMessages([]);
    LifeSciencesRagService.getMessages(requestedSession)
      .then(res => {
        if (activeSessionRef.current !== requestedSession) return; // user navigated away since
        const msgs = res.messages as ILsRagMessage[];
        setMessages(prev => {
          // Don't overwrite if user already sent a message while fetch was in flight
          const hasRealMessages = prev.some(m => m.id !== '__welcome__');
          if (hasRealMessages) return prev;
          return msgs.length ? msgs : [getWelcomeMsg(selectedAgent)];
        });
      })
      .catch(() => {
        if (activeSessionRef.current === requestedSession) setMessages([getWelcomeMsg(selectedAgent)]);
      });
  }, [activeSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewSession = async () => {
    try {
      const sess = await createSessionForAgent(AGENTS[selectedAgent]);
      setSessions(prev => [sess, ...prev]);
      autoScrollRef.current = false;
      activeSessionRef.current = sess.id;
      setActiveSession(sess.id);
      setMessages([getWelcomeMsg(selectedAgent)]);
    } catch { message.error('Could not create session'); }
  };

  const handleSend = useCallback(async (text?: string, relaxed?: boolean) => {
    const q = (text ?? input).trim();
    if (!q || querying) return;

    // Snapshot which session this specific send belongs to. If the user
    // navigates to a different session before the response comes back, we
    // must not force them back to this one or append the reply to whatever
    // session they're now looking at — the answer is already durably saved
    // server-side against requestSession regardless.
    const requestSession = activeSession;
    const userMsg: ILsRagMessage = {
      id: `tmp-${Date.now()}`, session_id: activeSession ?? '',
      role: 'user', content: q, created_at: new Date().toISOString(),
    };
    autoScrollRef.current = true;
    setMessages(prev => [...prev.filter(m => m.id !== '__welcome__'), userMsg]);
    setInput('');
    setQuerying(true);

    const currentAgent = AGENTS[selectedAgent];
    const agentId = currentAgent?.agentId ?? null;

    try {
      if (!currentAgent.isRag && agentId) {
        // Skills Engine agents (Migration Assistant, Landing Zone Advisor, DevOps Copilot) —
        // routed through the same tiered auto/manual model selection ls-rag uses.
        const res = await _skillsEngineChat(
          agentId, q, messages.filter(m => m.id !== '__welcome__'), activeSession,
          routingMode === 'manual' ? selectedModel : undefined, routingMode, disableCache,
        );
        const stillOnSameSession = activeSessionRef.current === requestSession;
        if (!requestSession) {
          _skillsEngineListSessions(agentId).then(r => setSessions(r.sessions)).catch(() => {});
          if (stillOnSameSession) {
            activeSessionRef.current = res.session_id;
            skipNextSessionLoadRef.current = true;
            setActiveSession(res.session_id);
          }
        }
        if (stillOnSameSession) {
          const assistantMsg: ILsRagMessage = {
            id: `tmp-a-${Date.now()}`,
            session_id: res.session_id || activeSession || '',
            role: 'assistant',
            content: res.text,
            model: res.model, tier: res.tier, cacheHit: res.cacheHit, cost: res.cost,
            created_at: new Date().toISOString(),
          };
          setMessages(prev => [...prev, assistantMsg]);
        }
        // Unconditional (not just the `!requestSession` branch above) —
        // sending into an already-active-but-still-"New Session" session
        // (e.g. one just created via the "New Agent Session" button) makes
        // the backend rename it server-side (rename_if_default), and this
        // is the only refetch that would ever pick that rename up.
        _skillsEngineListSessions(agentId).then(r => setSessions(r.sessions)).catch(() => {});
      } else {
        // Life Sciences RAG agent (index 0)
        const res = await LifeSciencesRagService.query(
          q, activeSession ?? undefined, relaxed,
          routingMode === 'manual' ? selectedModel : undefined, routingMode, disableCache,
        );
        const stillOnSameSession = activeSessionRef.current === requestSession;
        if (!requestSession) {
          LifeSciencesRagService.listSessions().then(r => setSessions(r.sessions)).catch(() => {});
          if (stillOnSameSession) {
            activeSessionRef.current = res.session_id;
            skipNextSessionLoadRef.current = true;
            setActiveSession(res.session_id);
          }
        }
        if (stillOnSameSession) {
          const assistantMsg: ILsRagMessage = {
            id: `tmp-a-${Date.now()}`, session_id: res.session_id,
            role: 'assistant', content: res.answer,
            sources: res.sources, groundedness: String(res.groundedness),
            created_at: new Date().toISOString(),
            model: res.model, tier: res.tier, cacheHit: res.cache_hit, cost: res.cost,
          };
          setMessages(prev => [...prev, assistantMsg]);
        }
        LifeSciencesRagService.listSessions().then(r => setSessions(r.sessions)).catch(() => {});
      }
    } catch (err: any) {
      message.error(err?.message ?? 'Query failed');
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally { setQuerying(false); }
  }, [input, querying, activeSession, routingMode, selectedModel, disableCache]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grows the input with its content (up to the CSS max-height, where it
  // starts scrolling internally instead) — without this a long pasted
  // prompt stays invisible past the single-line box's edge with no way to
  // see or scroll to the rest of it.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter inserts a newline; plain Enter sends (ignored mid-IME-composition
    // so composing CJK/accented text doesn't get submitted on the compositing Enter).
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteSession = async (sessId: string) => {
    setDeletingSess(sessId);
    try {
      await LifeSciencesRagService.deleteSession(sessId);
      setSessions(prev => prev.filter(s => s.id !== sessId));
      if (activeSession === sessId) { setActiveSession(null); setMessages([WELCOME]); }
    } catch (err: any) { message.error(err?.message ?? 'Could not delete session'); }
    finally { setDeletingSess(null); }
  };

  const handleDelete = async (docId: string) => {
    setDeletingDoc(docId);
    try {
      await LifeSciencesRagService.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      message.success('Document removed');
    } catch (err: any) { message.error(err?.message ?? 'Delete failed'); }
    finally { setDeletingDoc(null); }
  };

  const handleUpload = async (file: File) => {
    const drugName = window.prompt('Enter drug / document name:', file.name.replace('.pdf', ''));
    if (drugName === null) return;
    setUploading(true);
    try {
      await LifeSciencesRagService.uploadDocument(file, drugName || file.name);
      message.success(`${file.name} indexed successfully`);
      const res = await LifeSciencesRagService.listDocuments();
      setDocuments(res.documents);
    } catch (err: any) { message.error(err?.message ?? 'Upload failed'); }
    finally { setUploading(false); }
  };

  if (!open && !embedded) return null;

  const activeAg = AGENTS[selectedAgent];

  const hallucinationModal = showHallucinationModal ? (
    <HallucinationModal
      selectedIdx={selectedHQIdx}
      onSelectIdx={setSelectedHQIdx}
      onCancel={() => setShowHallucinationModal(false)}
      onSend={q => { setShowHallucinationModal(false); void handleSend(q, true); }}
    />
  ) : null;

  const body = (
    // span=5/19 matches the same Ant Grid columns Copilot Queries' sidebar/chat-panel
    // use, so the width is identical by construction, not calc()-approximation.
    <Row className={embedded ? 'cop-body cop-body--embedded' : 'cop-body'}>
      <Col span={5}>
        <AgentsSidebar
          selectedAgent={selectedAgent}
          setSelectedAgent={setSelectedAgent}
          sessions={sessions}
          activeSession={activeSession}
          setActiveSession={setActiveSession}
          deletingSess={deletingSess}
          handleDeleteSession={handleDeleteSession}
          documents={documents}
          uploading={uploading}
          handleUpload={handleUpload}
          deletingDoc={deletingDoc}
          handleDelete={handleDelete}
          handleNewSession={handleNewSession}
        />
      </Col>

      <Col span={19} className="cop-chat">
        <div className="chat-panel__header">
          <div className="chat-panel__header-left">
            <img src={DCAIcon} className="chat-panel__header-icon" alt="agent" />
            <div>
              <div className="chat-panel__header-title">
                {activeAg.initials === 'SL' ? 'Scientific Literature FAQ' : activeAg.name}
              </div>
              <div className="chat-panel__header-sub">
                <span className="chat-online-dot" />
                {'Online'}
                {activeAg.isRag && ' · pgvector retrieval active'}
              </div>
            </div>
          </div>
          {activeAg.supportsManualOverride && (
            <ModelBar
              routingMode={routingMode}
              setRoutingMode={setRoutingMode}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              disableCache={disableCache}
              setDisableCache={setDisableCache}
              allowedModels={activeAg.allowedModels}
            />
          )}
        </div>

        <div className="cop-msgs" ref={chatBodyRef}>
          {messages.map(msg => (
            <ChatMessage
              key={msg.id}
              msg={msg}
              expandedSources={expandedSources}
              onToggleSource={toggleSource}
              showAgentTag={selectedAgent === 0}
              agentLabel={activeAg.initials === 'SL' ? 'Scientific Literature FAQ' : activeAg.name}
            />
          ))}
          {querying && (
            <div className="cmsg cmsg-asst">
              <img src={DCAIcon} className="msg-av" alt="agent" />
              <div className="typing-dots"><span /><span /><span /></div>
            </div>
          )}
        </div>

        {activeAg.isRag && (
          <div className="cop-hints">
            {HINTS.map(h => <div key={h} className="hint" onClick={() => handleSend(h)}>{h}</div>)}
            <div
              className="hint hint-adv"
              title="Open query picker — sends with relaxed guardrails to demonstrate hallucination in observability"
              onClick={() => { setSelectedHQIdx(0); setShowHallucinationModal(true); }}
            >
              ⚡ Induce Hallucination
            </div>
          </div>
        )}

        <div className="cop-inp-area">
          <img src={DCAIcon} className="cop-inp-avatar" alt="DCA" />
          <div className="cop-inp-row">
            <textarea
              ref={textareaRef}
              className="cop-inp"
              placeholder="Start typing..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={querying}
              rows={1}
            />
            {activeAg.supportsCostEstimate && (
              <CostEstimator
                input={input} disableCache={disableCache} routingMode={routingMode} selectedModel={selectedModel}
                agentId={activeAg.isRag ? null : activeAg.agentId}
                history={messages}
              />
            )}
            <button className="cop-send" onClick={() => handleSend()} disabled={!input.trim() || querying}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      </Col>
    </Row>
  );

  if (embedded) {
    return (
      <div className="cop-overlay" style={{ position: 'relative', inset: 'auto', zIndex: 'auto', height: '100%' }}>
        {hallucinationModal}
        {body}
      </div>
    );
  }

  return (
    <div className="cop-overlay">
      {hallucinationModal}
      <div className="cop-hdr">
        <div className="cop-brand">
          <div className="cop-logo">DCA</div>
          DCA Copilot
        </div>
        <div className="cop-tabs">
          <div className="ctab">Copilot Queries</div>
          <div className="ctab">Document Analysis</div>
          <div className="ctab">Skills Assistant</div>
          <div className="ctab on">Agents ✦</div>
        </div>
        <div className="cop-hdr-r">
          <div className="cop-av">SG</div>
        </div>
        <div className="cop-x" onClick={onClose}>✕</div>
      </div>
      {body}
    </div>
  );
};

export default CopilotAgents;
